/**
 * Transaction Progress Content
 *
 * State-based content component for the transaction dialog.
 * Shows executing, confirmed, or failed states.
 */

import { CheckCircle2, XCircle, Loader2, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTransactionDialog } from '@/hooks/useTransactionDialog';
import { Button } from '@/components/ui/button';
import { getSolscanUrl } from '@/lib/solana/config';
import { cn } from '@/lib/utils';

function getActionDescription(
  type: string,
  metadata: { tokenName?: string; tokenSymbol?: string; amount?: number; amountOut?: number } | null
): string {
  if (!metadata) return 'Processing transaction...';

  const { tokenName, tokenSymbol, amount, amountOut } = metadata;
  const symbol = tokenSymbol || tokenName || 'tokens';

  switch (type) {
    case 'create-coin':
      return `Creating coin: ${tokenName || 'Unknown'} [${tokenSymbol || '???'}]`;
    case 'quick-buy':
      return `Buying ${amount?.toFixed(4) || '?'} SOL of ${symbol}`;
    case 'quick-sell':
      if (amount && amountOut) {
        return `Selling ${amount.toLocaleString()} ${symbol} for ~${amountOut?.toFixed(4) ?? '?'} SOL`;
      }
      return `Selling ${symbol}...`;
    case 'claim-rewards':
      return `Claiming ${amount?.toFixed(4) || ''} SOL...`;
    default:
      return 'Processing transaction...';
  }
}

function getSuccessDescription(
  type: string,
  metadata: { tokenName?: string; tokenSymbol?: string; amount?: number; amountOut?: number } | null
): string {
  if (!metadata) return 'Transaction confirmed!';

  const { tokenName, tokenSymbol, amount, amountOut } = metadata;
  const symbol = tokenSymbol || 'coins';

  switch (type) {
    case 'create-coin':
      return `${tokenName || tokenSymbol || 'Coin'} has been launched!`;
    case 'quick-buy':
      if (amountOut) {
        return `Bought ${amountOut.toLocaleString()} ${symbol} for ${amount?.toFixed(4) || '?'} SOL`;
      }
      return `Successfully bought ${symbol}`;
    case 'quick-sell':
      if (amountOut) {
        return `Sold ${amount?.toLocaleString() || ''} ${symbol} for ${amountOut?.toFixed(4) ?? '?'} SOL`;
      }
      return `Successfully sold ${symbol}`;
    case 'claim-rewards':
      return `Claimed ${amount?.toFixed(4) || '?'} SOL`;
    default:
      return 'Transaction confirmed!';
  }
}

function ExecutingContent() {
  const { metadata } = useTransactionDialog();
  const description = getActionDescription(metadata?.type || 'generic', metadata);

  return (
    <div className="p-6">
      {/* Spinner with pulse animation */}
      <div className="flex justify-center mb-4">
        <div className="relative">
          <Loader2 className="w-12 h-12 text-[#CCFF00] animate-spin" />
          <div className="absolute inset-0 w-12 h-12 rounded-full bg-[#CCFF00]/20 animate-pulse" />
        </div>
      </div>

      {/* Action description */}
      <p
        id="transaction-dialog-title"
        className="text-center text-lg font-medium text-white mb-4"
      >
        {description}
      </p>

      {/* Progress bar */}
      <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div
          className="h-full bg-[#CCFF00] rounded-full animate-progress-indeterminate"
          style={{
            animation: 'progress-indeterminate 1.5s ease-in-out infinite',
          }}
        />
      </div>

      <style>{`
        @keyframes progress-indeterminate {
          0% {
            width: 0%;
            margin-left: 0%;
          }
          50% {
            width: 60%;
            margin-left: 20%;
          }
          100% {
            width: 0%;
            margin-left: 100%;
          }
        }
      `}</style>
    </div>
  );
}

function ConfirmedContent() {
  const { metadata, countdownSeconds, dismiss } = useTransactionDialog();
  const navigate = useNavigate();
  const description = getSuccessDescription(metadata?.type || 'generic', metadata);

  const handleViewTx = () => {
    if (metadata?.signature) {
      window.open(getSolscanUrl(metadata.signature, 'tx'), '_blank');
    }
  };

  const handleViewCoin = () => {
    if (metadata?.mintAddress) {
      dismiss();
      navigate(`/tokens/${metadata.mintAddress}`);
    }
  };

  return (
    <div className="p-6">
      {/* Success icon */}
      <div className="flex justify-center mb-4">
        <div className="relative">
          <CheckCircle2 className="w-12 h-12 text-green-500" />
          <div className="absolute inset-0 w-12 h-12 rounded-full bg-green-500/20 animate-pulse" />
        </div>
      </div>

      {/* Success message */}
      <p
        id="transaction-dialog-title"
        className="text-center text-lg font-medium text-white mb-4"
      >
        {description}
      </p>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 justify-center">
        {metadata?.signature && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleViewTx}
            className="gap-1.5"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            View tx
          </Button>
        )}

        {metadata?.mintAddress && metadata?.type === 'create-coin' && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleViewCoin}
            className="gap-1.5"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            View coin
          </Button>
        )}

        <Button
          variant="ghost"
          size="sm"
          onClick={dismiss}
          className="text-muted-foreground"
        >
          Dismiss{countdownSeconds !== null ? ` (${countdownSeconds}s)` : ''}
        </Button>
      </div>
    </div>
  );
}

function FailedContent() {
  const { metadata, dismiss } = useTransactionDialog();
  const errorMessage = metadata?.errorMessage || 'Transaction failed';

  // Check for common error types
  const isUserRejection =
    errorMessage.toLowerCase().includes('rejected') ||
    errorMessage.toLowerCase().includes('cancelled') ||
    errorMessage.toLowerCase().includes('canceled');

  const displayMessage = isUserRejection
    ? 'You cancelled the transaction'
    : errorMessage;

  return (
    <div className="p-6">
      {/* Error icon */}
      <div className="flex justify-center mb-4">
        <div className="relative">
          <XCircle className="w-12 h-12 text-red-500" />
          <div className="absolute inset-0 w-12 h-12 rounded-full bg-red-500/20" />
        </div>
      </div>

      {/* Error message */}
      <p
        id="transaction-dialog-title"
        className="text-center text-lg font-medium text-white mb-2"
      >
        Transaction failed
      </p>
      <p className="text-center text-sm text-muted-foreground mb-4 max-w-[300px] mx-auto">
        {displayMessage}
      </p>

      {/* Action buttons */}
      <div className="flex gap-2 justify-center">
        <Button
          variant="ghost"
          size="sm"
          onClick={dismiss}
          className="text-muted-foreground"
        >
          Dismiss
        </Button>
      </div>
    </div>
  );
}

export function TransactionProgressContent() {
  const { state } = useTransactionDialog();

  switch (state) {
    case 'executing':
      return <ExecutingContent />;
    case 'confirmed':
      return <ConfirmedContent />;
    case 'failed':
      return <FailedContent />;
    default:
      return null;
  }
}
